import { Box, Skeleton, Typography, useMediaQuery } from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";
import { ILoginActivity } from "../../../models/admin/loginActivity/loginActivity";
import { FC } from "react";
import { useAppSelector } from "../../../store/hooks";
import { RootState } from "../../../store/store";

interface IActivityDistributionProps {
  filteredData: ILoginActivity[];
}

// Update processData function to handle the ILoginActivity interface
const processData = (data: ILoginActivity[]) => {
  const result = {
    login: 0,
    logout: 0,
    failed: 0,
  };

  data.forEach((entry) => {
    if (entry.comments === "SUCCESSFUL_LOGIN" && entry.loginTime) {
      result.login += 1;
    } else if (entry.comments === "LOGOUT_SUCCESS" && entry.logoutTime) {
      result.logout += 1;
    } else if (entry.activity?.toLowerCase() === "failed") {
      result.failed += 1;
    } else if (
      entry.activity?.toLowerCase() === "success" &&
      !entry.logoutTime
    ) {
      result.login += 1;
    }
  });

  return [
    { name: "Login", value: result.login },
    { name: "Logout", value: result.logout },
    { name: "Failed", value: result.failed },
  ];
};

const COLORS = ["#4CAF50", "#FF9800", "#F44336"];

const ActivityDistribution: FC<IActivityDistributionProps> = ({
  filteredData = [],
}) => {
  const isMobile = useMediaQuery("(max-width:767px)");
  const { isLoading } = useAppSelector((store: RootState) => store.common);

  // Process the actual filteredData instead of rawData
  const data = processData(filteredData);

  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor: "white",
        padding: 2,
        borderRadius: 2,
        boxShadow: 1,
      }}
      id="activity_distribution_container"
      data-test-id="activity_distribution_container"
      aria-label="Activity Distribution Chart"
    >
      <Typography 
        sx={{ fontWeight: "600", marginBottom: 2 }}
        id="activity_distribution_title"
        data-test-id="activity_distribution_title"
        aria-label="Activity Distribution"
      >
        Activity Distribution
      </Typography>
      {isLoading ? (
        <Skeleton
          variant="rounded"
          height="250px"
          animation="wave"
          sx={{
            width: isMobile ? "100%" : "auto",
            minWidth: isMobile ? "100%" : "466px",
            flex: 1,
          }}
          id="activity_distribution_skeleton"
          data-test-id="activity_distribution_skeleton"
          aria-label="Loading Activity Distribution"
        />
      ) : (
        <Box 
          sx={{ height: 300 }}
          id="activity_distribution_chart"
          data-test-id="activity_distribution_chart"
          aria-label="Activity Distribution Chart Content"
        >
          {data.every((entry) => entry.value === 0) || data.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
              id="activity_distribution_empty"
              data-test-id="activity_distribution_empty"
              aria-label="No Activity Records Found"
            >
              <Typography
                sx={{ textAlign: "center", fontSize: 16, color: "black" }}
                id="activity_distribution_empty_title"
                data-test-id="activity_distribution_empty_title"
                aria-label="No Activity Records Found"
              >
                No records found
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  fill="#8884d8"
                  paddingAngle={5}
                  dataKey="value"
                  id="activity_distribution_pie"
                  data-test-id="activity_distribution_pie"
                  aria-label="Activity Distribution Pie Chart"
                >
                  {data.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip />
                <Legend
                  layout="horizontal"
                  align="center"
                  verticalAlign="bottom"
                  iconType="circle"
                  iconSize={12}
                  wrapperStyle={{
                    paddingTop: "10px", // Adds some spacing above the legend
                  }}
                  id="activity_distribution_legend"
                  data-test-id="activity_distribution_legend"
                  aria-label="Activity Distribution Legend"
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ActivityDistribution;
